import { FilingsService } from '../filings.service';
import { FilingSource, MailChangedError, MailMetadata } from './filing-source';
import { FiledTarget } from './mail-upload.models';
import { MailService } from './mail.service';

/**
 * The mail open in Outlook, as it was when the user clicked to file it.
 *
 * A pinned pane lets the user move to another mail at any point of a filing,
 * Outlook swapping the item underneath. So the metadata is taken on creation:
 * these are synchronous read-mode properties, and reading them later could
 * describe a different message than the EML does. Reading the EML fails if the
 * mail changed meanwhile, and the writes after the upload skip a mail no longer
 * open rather than landing on the next one.
 */
export class CurrentMailSource implements FilingSource {
  public readonly id: string;

  private readonly _itemId: string | null;
  private readonly _metadata: Omit<MailMetadata, 'sentDateTime'>;
  /** Asked for at once, so it comes from the same mail as the rest. */
  private readonly _sentDateTime: Promise<string | null>;

  public constructor(
    private readonly _mailService: MailService,
    private readonly _filingsService: FilingsService
  ) {
    this._itemId = _mailService.getCurrentItemId();
    this.id = this._itemId ?? '';
    this._metadata = {
      subject: _mailService.getCurrentMailSubject(),
      ..._mailService.getCurrentMailMetadata()
    };
    this._sentDateTime = _mailService.getCurrentMailSentDateTime();
  }

  public async getMetadata(): Promise<MailMetadata> {
    return { ...this._metadata, sentDateTime: await this._sentDateTime };
  }

  public getFilings(): Promise<FiledTarget[]> {
    return this._mailService.getCurrentMailFilings();
  }

  public async getEmlBase64(): Promise<string> {
    const eml = await this._mailService.getCurrentMailEmlBase64();

    // Reading the EML is the one slow step here, and long enough on a large mail
    // for the user to move on to the next one. Filing what was read then means
    // filing a mail they are no longer looking at.
    if (!this._isStillOpen()) {
      throw new MailChangedError('The open mail changed while it was read');
    }

    return eml;
  }

  /**
   * Goes through FilingsService, which lists the filing at once, before its
   * first await, so a confirmation shown right after this call has it.
   */
  public recordFiling(target: FiledTarget): Promise<void> {
    if (!this._isStillOpen()) {
      return Promise.resolve();
    }

    return this._filingsService.record(target);
  }

  /** Marking goes through the currently selected item, so only while it is this one. */
  public markFiled(): Promise<void> {
    if (!this._isStillOpen()) {
      return Promise.resolve();
    }

    return this._mailService.markCurrentMailAsFiled();
  }

  private _isStillOpen(): boolean {
    return this._mailService.getCurrentItemId() === this._itemId;
  }
}
